import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useUIStore } from '../../store/uiStore';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { cn } from '../../utils/cn';
import Header from './Header';
import Sidebar from './Sidebar';

/**
 * LayoutShell — responsive layout with smooth sidebar transitions.
 *
 * Desktop (≥1024px): sidebar slides in/out with smooth width transition;
 *   main content adjusts fluidly via flex layout.
 * Tablet (640–1023px): sidebar as overlay drawer with slide animation.
 * Mobile (<640px): sidebar as full-screen overlay.
 */
export default function LayoutShell() {
  const { sidebarOpen, sidebarMode } = useUIStore();
  const { isDesktop, isTablet } = useMediaQuery();

  const setSidebarMode = useUIStore((s) => s.setSidebarMode);
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);

  useEffect(() => {
    if (isDesktop) {
      setSidebarMode('persistent');
      setSidebarOpen(true);
    } else if (isTablet) {
      setSidebarMode('overlay');
      setSidebarOpen(false);
    } else {
      setSidebarMode('fullscreen');
      setSidebarOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPersistent = sidebarMode === 'persistent';
  const showOverlay =
    (sidebarMode === 'overlay' || sidebarMode === 'fullscreen') && sidebarOpen;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Header — full width, fixed height */}
      <Header />

      {/* Body: sidebar + main — fills remaining height */}
      <div className="flex flex-1 min-h-0">
        {/* Persistent sidebar (desktop) — always in DOM for smooth transition */}
        {isPersistent && (
          <div
            className={cn(
              'shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out',
              sidebarOpen ? 'w-[280px]' : 'w-0'
            )}
          >
            <div className="w-[280px] h-full overflow-y-auto">
              <Sidebar />
            </div>
          </div>
        )}

        {/* Overlay sidebar (tablet / mobile) */}
        {showOverlay && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/40 transition-opacity duration-200"
              onClick={() => useUIStore.getState().setSidebarOpen(false)}
              aria-hidden="true"
            />
            <div
              className={cn(
                'fixed top-0 left-0 z-50 h-full bg-white shadow-xl transition-transform duration-300 ease-in-out',
                sidebarMode === 'fullscreen' ? 'w-full' : 'w-[280px]',
              )}
            >
              <Sidebar />
            </div>
          </>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-y-auto min-h-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
